// File: src/components/TabbedStandingsView.js
// Tabbed interface for all championship views.
//
// It is handed a CLASS, not a series - a class is what has standings. Nothing
// inside had to change for that: the server resolves each class's scoring rules
// against its series and spreads them across the object under the same names a
// series uses, so every read here still finds what it is looking for.

import React, { useState } from 'react';
import { Trophy, Award, Users, ListOrdered } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateForDisplay } from '../utils/utils';
import { calculateStandings, racesRun } from '../utils/standings';

function TabbedStandingsView({ seriesClass, isPublic }) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('championship');

  // Championship standings live in utils/standings.js so the arithmetic can
  // be tested directly.
  const standings = calculateStandings(seriesClass);

  // The same rounds the standings are built from. A round that an event class
  // has claimed but nobody has raced yet is not a column with everyone on zero,
  // it is a race that has not happened - and the per-driver scores below have
  // no cell for it, so a header here would shift every column out of line.
  const races = racesRun(seriesClass);

  // Calculate qualification master standings
  const calculateQualificationMasterStandings = () => {
    const driverStats = {};

    seriesClass.drivers?.forEach(driver => {
      driverStats[driver.id] = {
        name: driver.name,
        club: driver.club,
        totalQualPoints: 0
      };
    });

    races.forEach(race => {
      race.rankings?.forEach(ranking => {
        if (driverStats[ranking.driver_id]) {
          driverStats[ranking.driver_id].totalQualPoints += ranking.qualification_points || 0;
        }
      });
    });

    return Object.values(driverStats)
      .filter(d => d.totalQualPoints > 0)
      .sort((a, b) => b.totalQualPoints - a.totalQualPoints);
  };

  // Calculate senior standings
  const calculateSeniorStandings = () => {
    const currentYear = new Date().getFullYear();
    const seniorAgeLimit = seriesClass.senior_age_limit || 40;
    

    return standings.filter(driver => {
      if (!driver.birthYear) return false;
      const age = currentYear - driver.birthYear;
      return age >= seniorAgeLimit;
    });
  };

  // Calculate junior standings
  const calculateJuniorStandings = () => {
    const currentYear = new Date().getFullYear();
    const juniorAgeLimit = seriesClass.junior_age_limit || 18;
    

    return standings.filter(driver => {
      if (!driver.birthYear) return false;
      const age = currentYear - driver.birthYear;
      return age <= juniorAgeLimit;
    });
  };

  
  const qualMasterStandings = seriesClass.qualification_master ? calculateQualificationMasterStandings() : null;
  const seniorStandings = seriesClass.senior_championship ? calculateSeniorStandings() : null;
  const juniorStandings = seriesClass.junior_championship ? calculateJuniorStandings() : null;

  const tabs = [
    { id: 'championship', label: t('championshipStandings'), icon: Trophy, show: true },
    { id: 'qualification', label: t('qualificationMaster'), icon: Award, show: seriesClass.qualification_master },
    { id: 'junior', label: t('juniorStandings'), icon: Users, show: seriesClass.junior_championship },
    { id: 'senior', label: t('seniorStandings'), icon: Users, show: seriesClass.senior_championship },
    { id: 'races', label: t('raceResults'), icon: ListOrdered, show: true }
  ].filter(tab => tab.show);

  const categoryStyles = {
    yellow: { rowHighlight: 'bg-amber-50 dark:bg-amber-500/20 border border-amber-500/50', text: 'text-amber-700 dark:text-amber-400' },
    green: { rowHighlight: 'bg-green-50 dark:bg-green-500/20 border border-green-500/50', text: 'text-green-700 dark:text-green-400' },
    purple: { rowHighlight: 'bg-purple-50 dark:bg-purple-500/20 border border-purple-500/50', text: 'text-purple-700 dark:text-purple-400' },
    blue: { rowHighlight: 'bg-blue-50 dark:bg-blue-500/20 border border-blue-500/50', text: 'text-blue-700 dark:text-blue-400' },
  };

  const renderDriverRow = (driver, index, highlightColor = 'yellow') => (
    <div
      key={index}
      className={`flex items-center justify-between p-4 rounded ${
        index === 0 ? categoryStyles[highlightColor].rowHighlight :
        index === 1 ? 'bg-gray-100 dark:bg-gray-500/20 border border-gray-400/50 dark:border-gray-500/50' :
        index === 2 ? 'bg-orange-50 dark:bg-orange-500/20 border border-orange-500/50' :
        'bg-paper-alt'
      }`}
    >
      <div className="flex items-center gap-4">
        <span className="text-2xl font-bold text-ink w-8 font-mono-num">{index + 1}</span>
        <div>
          <span className="text-xl text-ink font-semibold">{driver.name}</span>
          {driver.club && <div className="text-ink-muted text-sm">{driver.club}</div>}
          {!isPublic && driver.birthYear && (
            <div className="text-ink-muted text-xs">{t('born')} {driver.birthYear}</div>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className={`text-2xl font-bold font-mono-num ${categoryStyles[highlightColor].text}`}>
          {driver.totalPoints || driver.totalQualPoints} {t('points')}
        </div>
        {seriesClass.calculate_qualification_points && driver.racePoints !== undefined && (driver.racePoints > 0 || driver.qualificationPoints > 0) && (
          <div className="text-sm text-ink-muted font-mono-num">
            {t('raceShort')}: {driver.racePoints}p + {t('qualShort')}: {driver.qualificationPoints}p
          </div>
        )}
        {(seriesClass.races_counted || 0) > 0 && driver.raceScores?.some(s => s.dropped) && (
          <div className="text-xs text-red-700 dark:text-red-400">
            ({driver.raceScores.filter(s => s.dropped).length} {t('dropped')})
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-paper rounded-lg border border-line">
      {/* Tab Navigation */}
      <div className="border-b border-line">
        <div className="flex overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-paper-alt text-ink border-b-2 border-accent'
                    : 'text-ink-muted hover:text-ink hover:bg-paper-alt'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {/* Championship Standings Tab */}
        {activeTab === 'championship' && (
          <div>
            <h2 className="text-2xl font-bold text-ink mb-2">{t('championshipStandings')}</h2>
            {seriesClass.total_races > 0 && (
              <p className="text-ink-muted mb-4 font-mono-num">
                {seriesClass.total_races} {t('racesInSeries')} · {t('best')} {seriesClass.races_counted || seriesClass.total_races} {t('countedLabel')}
              </p>
            )}
            <div className="space-y-3">
              {standings.map((driver, index) => renderDriverRow(driver, index, 'yellow'))}
              {standings.length === 0 && (
                <p className="text-ink-muted text-center py-8">{t('noStandingsYet')}</p>
              )}
            </div>
          </div>
        )}

        {/* Qualification Master Tab */}
        {activeTab === 'qualification' && qualMasterStandings && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-ink">🏆 {t('qualificationMasterStandings')}</h2>
              <span className="bg-green-600 text-white text-sm px-3 py-1 rounded-full font-semibold">
                {t('allQualificationPoints')}
              </span>
            </div>
            <div className="bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200 px-4 py-3 rounded mb-4">
              <strong>{t('pureQualificationChampionship')}</strong> {t('allQualificationPointsCounted')}
            </div>
            <div className="space-y-3">
              {qualMasterStandings.map((driver, index) => renderDriverRow(driver, index, 'green'))}
              {qualMasterStandings.length === 0 && (
                <p className="text-ink-muted text-center py-8">{t('noQualificationDataYet')}</p>
              )}
            </div>
          </div>
        )}

        {/* Junior Championship Tab */}
        {activeTab === 'junior' && juniorStandings && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-ink">🧒 {t('juniorChampionshipStandings')}</h2>
              <span className="bg-purple-600 text-white text-sm px-3 py-1 rounded-full font-semibold">
                {seriesClass.junior_age_limit || 18} {t('ageAndUnder')}
              </span>
            </div>
            <div className="bg-purple-50 dark:bg-purple-500/20 border border-purple-500 text-purple-800 dark:text-purple-200 px-4 py-3 rounded mb-4">
              <strong>{t('juniorDriversOnly')}</strong> {t('onlyDriversAgedIncluded')} {seriesClass.junior_age_limit || 18} {t('andUnderIncluded')}
            </div>
            <div className="space-y-3">
              {juniorStandings.map((driver, index) => renderDriverRow(driver, index, 'purple'))}
              {juniorStandings.length === 0 && (
                <p className="text-ink-muted text-center py-8">{t('noJuniorDriversYet')}</p>
              )}
            </div>
          </div>
        )}

        {/* Senior Championship Tab */}
        {activeTab === 'senior' && seniorStandings && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-ink">👴 {t('seniorChampionshipStandings')}</h2>
              <span className="bg-blue-600 text-white text-sm px-3 py-1 rounded-full font-semibold">
                {seriesClass.senior_age_limit || 40}{t('agePlus')}
              </span>
            </div>
            <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200 px-4 py-3 rounded mb-4">
              <strong>{t('seniorDriversOnly')}</strong> {t('onlyDriversAgedIncluded')} {seriesClass.senior_age_limit || 40}{t('andOverIncluded')}
            </div>
            <div className="space-y-3">
              {seniorStandings.map((driver, index) => renderDriverRow(driver, index, 'blue'))}
              {seniorStandings.length === 0 && (
                <p className="text-ink-muted text-center py-8">{t('noSeniorDriversYet')}</p>
              )}
            </div>
          </div>
        )}

        {/* Race Results Tab */}
        {activeTab === 'races' && (
          <div>
            <h2 className="text-2xl font-bold text-ink mb-6">{t('raceResults')}</h2>
            {standings.length === 0 ? (
              <p className="text-ink-muted text-center py-8">{t('noStandingsYet')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-line">
                      <th className="px-3 py-3 text-left text-ink-muted font-semibold sticky left-0 bg-paper">{t('position')}</th>
                      <th className="px-3 py-3 text-left text-ink-muted font-semibold sticky left-12 bg-paper">{t('driverName')}</th>
                      <th className="px-3 py-3 text-left text-ink-muted font-semibold">{t('club')}</th>
                      {races.map((race, idx) => (
                        <th key={race.id} className="px-3 py-3 text-center text-ink-muted font-semibold min-w-[100px]">
                          <div className="text-sm">{race.name}</div>
                          {race.date && (
                            <div className="text-xs text-ink-muted font-normal">
                              {formatDateForDisplay(race.date)}
                            </div>
                          )}
                        </th>
                      ))}
                      <th className="px-3 py-3 text-center text-ink-muted font-semibold bg-paper-alt">{t('total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((driver, standingIndex) => {
                      // Sort driver's race scores by race date to match header order
                      const sortedScores = [...driver.raceScores].sort((a, b) => {
                        const raceA = races.find(r => r.id === a.raceId);
                        const raceB = races.find(r => r.id === b.raceId);
                        return new Date(raceA?.date || 0) - new Date(raceB?.date || 0);
                      });

                      return (
                        <tr
                          key={standingIndex}
                          className={`border-b border-line ${
                            standingIndex === 0 ? 'bg-yellow-50 dark:bg-yellow-500/10' :
                            standingIndex === 1 ? 'bg-gray-100 dark:bg-gray-500/10' :
                            standingIndex === 2 ? 'bg-orange-50 dark:bg-orange-500/10' :
                            'hover:bg-paper-alt'
                          }`}
                        >
                          <td className="px-3 py-4 sticky left-0 bg-inherit">
                            <span className={`text-xl font-bold font-mono-num ${
                              standingIndex === 0 ? 'text-yellow-700 dark:text-yellow-400' :
                              standingIndex === 1 ? 'text-gray-700 dark:text-gray-300' :
                              standingIndex === 2 ? 'text-orange-700 dark:text-orange-400' :
                              'text-ink'
                            }`}>
                              {standingIndex + 1}
                            </span>
                          </td>
                          <td className="px-3 py-4 sticky left-12 bg-inherit">
                            <div className="text-ink font-semibold">{driver.name}</div>
                          </td>
                          <td className="px-3 py-4 text-ink-muted text-sm">{driver.club || '-'}</td>
                          {sortedScores.map((score, scoreIdx) => (
                            <td key={scoreIdx} className="px-3 py-4 text-center">
                              {score.participated ? (
                                <div className={score.dropped ? 'opacity-40' : ''}>
                                  <div className={`text-sm font-semibold font-mono-num ${
                                    score.dropped ? 'text-red-700 dark:text-red-400 line-through' :
                                    score.position === 1 ? 'text-yellow-700 dark:text-yellow-400' :
                                    score.position === 2 ? 'text-gray-700 dark:text-gray-300' :
                                    score.position === 3 ? 'text-orange-700 dark:text-orange-400' :
                                    'text-ink'
                                  }`}>
                                    P{score.position}
                                  </div>
                                  <div className={`text-xs font-mono-num ${score.dropped ? 'text-red-700 dark:text-red-400 line-through' : 'text-ink-muted'}`}>
                                    {score.total}p
                                  </div>
                                  {score.dropped && (
                                    <div className="text-[10px] text-red-700 dark:text-red-500 font-semibold uppercase">{t('dropped')}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-ink-muted text-sm">-</span>
                              )}
                            </td>
                          ))}
                          <td className="px-3 py-4 text-center bg-paper-alt">
                            <div className="text-xl font-bold text-accent font-mono-num">{driver.totalPoints}</div>
                            {seriesClass.calculate_qualification_points && (driver.racePoints > 0 || driver.qualificationPoints > 0) && (
                              <div className="text-[10px] text-ink-muted font-mono-num">
                                R:{driver.racePoints} Q:{driver.qualificationPoints}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TabbedStandingsView;
